<template>
  <!-- Renders all components without data instantly, loads first 5 on server, lazy loads the rest -->
  <template v-for="(componentData, index) in computedContent" :key="getKey(componentData)">
    <template v-if="componentData.collection">
      <template v-if="isNoDataComponent(componentData)">
        <DynamicComponent v-bind="getComponentProps(componentData, index)" />
      </template>
      <TransitionHeight v-else-if="componentData.id && index < MAX_SSR_COMPONENTS - startIndex">
        <DynamicComponent v-bind="getComponentProps(componentData, index)" />
      </TransitionHeight>
      <client-only v-else>
        <Suspense>
          <template #default>
            <DynamicComponent v-bind="getComponentProps(componentData, index)" />
          </template>
          <template #fallback>
            <Transition name="fade" mode="out-in">
              <!-- eslint-disable-next-line -->
              <div class="loader">
                <Suspense>
                  <Spinner />
                </Suspense>
              </div>
            </Transition>
          </template>
        </Suspense>
      </client-only>
    </template>
  </template>
</template>

<script setup lang="ts">
import useDynamicComponent from '~/composables/useDynamicComponent';
import formatUtils from '~/utils/formatUtils';
import type {
  ICmsArticlesContent,
  ICmsDossiersContent,
  ICmsHomeSections,
} from '~~/types/_cms-types';

interface IProps {
  content: Array<
    ICmsArticlesContent | ICmsHomeSections | (ICmsDossiersContent & { staticData?: never })
  >;
  m2aField: string;
  startIndex?: number;
}

const MAX_SSR_COMPONENTS = 5;
const props = withDefaults(defineProps<IProps>(), { startIndex: 0 });

const { toCamelCase } = formatUtils();

const resolveComponentDefinition = (collection: string) =>
  useDynamicComponent(toCamelCase(collection));

// Only deduplicate these
const singletonComponents = ['content_chats'];

const seen = new Set();
const computedContent = computed(() => {
  return props.content?.filter((c) => {
    if (singletonComponents.includes(c.collection)) {
      if (seen.has(c.collection)) return false;
      seen.add(c.collection);
    }
    return true;
  });
});

const getKey = (componentData: never) =>
  componentData.id ? `${componentData.collection}-${componentData.id}` : `fallback-key`;

const isNoDataComponent = (componentData: never) => {
  const definition = resolveComponentDefinition(componentData.collection);
  return definition?.noData;
};

const getComponentProps = (componentData: never, index: number) => ({
  collection: componentData.collection,
  item: Number(componentData.id),
  _index: props.startIndex + index,
  m2aField: props.m2aField,
  staticData: componentData.staticData ?? componentData,
});
</script>

<style scoped lang="postcss">
.loader {
  padding: var(--unit-60);
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
}
</style>
